import React, { useState, useEffect, useCallback } from 'react';
import { Siren, Phone, X, Trash2 } from 'lucide-react';
import api from '../utils/api';
import { fechaCL, hoyChile } from '../utils/fecha';

// Técnico de turno de emergencias. Se muestra en Planificación (todos los roles;
// admin además puede gestionarlo) y en el Home de la central.
// Fuente: GET /planificacion/turno-emergencia → { actual, proximo }.

const rango = t => t.fecha_desde === t.fecha_hasta
  ? fechaCL(t.fecha_desde)
  : `${fechaCL(t.fecha_desde)} → ${fechaCL(t.fecha_hasta)}`;

export default function TurnoEmergencia({ puedeGestionar = false, compacto = false }) {
  const [data, setData]   = useState(null);
  const [modal, setModal] = useState(false);

  const cargar = useCallback(() => {
    api.get('/planificacion/turno-emergencia')
      .then(r => setData(r.data))
      .catch(() => setData({ actual: null, proximo: null }));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (!data) return null;
  const { actual, proximo } = data;
  const tel = actual?.telefono;

  return (
    <>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        background: actual ? '#1A0808' : '#161C19',
        border: `1px solid ${actual ? '#3D1515' : '#2E4A40'}`,
        borderRadius: compacto ? 0 : 14,
        padding: '12px 16px', marginBottom: compacto ? 0 : 8,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: actual ? '#2D0F0F' : '#1E2922',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Siren size={18} color={actual ? '#EF4444' : '#6B7C74'} strokeWidth={2} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, color: '#8A9994', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 700 }}>
            Turno de emergencias
          </div>
          {actual ? (
            <>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#F4F7F5', marginTop: 2 }}>
                {actual.tecnico_nombre}
              </div>
              <div style={{ fontSize: 12, color: '#EF9A9A', marginTop: 2 }}>
                {rango(actual)}{actual.nota ? ` · ${actual.nota}` : ''}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 14, color: '#F59E0B', marginTop: 2, fontWeight: 600 }}>
              Sin técnico de turno asignado hoy
            </div>
          )}
          {proximo && (
            <div style={{ fontSize: 11, color: '#6B7C74', marginTop: 3 }}>
              Próximo: {proximo.tecnico_nombre} ({rango(proximo)})
            </div>
          )}
        </div>
        {tel && (
          <a href={`tel:${tel.replace(/\s+/g, '')}`} title={`Llamar ${tel}`} style={{
            display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
            background: '#EF4444', color: 'white', borderRadius: 10,
            padding: '8px 12px', fontSize: 13, fontWeight: 700, textDecoration: 'none',
          }}>
            <Phone size={14} /> {tel}
          </a>
        )}
        {puedeGestionar && (
          <button onClick={() => setModal(true)} style={{
            background: '#161C19', border: '1.5px solid #2E4A40', color: '#C9D3CE',
            borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 700,
            cursor: 'pointer', flexShrink: 0,
          }}>
            Gestionar
          </button>
        )}
      </div>

      {modal && (
        <ModalTurnos onClose={() => setModal(false)} onCambio={cargar} />
      )}
    </>
  );
}

function ModalTurnos({ onClose, onCambio }) {
  const [turnos, setTurnos]     = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [form, setForm] = useState({
    tecnico_id: '', fecha_desde: hoyChile(), fecha_hasta: hoyChile(), telefono: '', nota: '',
  });
  const [guardando, setGuardando] = useState(false);

  const cargarTurnos = useCallback(() => {
    api.get('/planificacion/turnos-emergencia').then(r => setTurnos(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    cargarTurnos();
    api.get('/admin/tecnicos')
      .then(r => setTecnicos(r.data.filter(t => t.activo && ['tecnico', 'admin', 'superadmin'].includes(t.rol))))
      .catch(() => {});
  }, [cargarTurnos]);

  const guardar = async () => {
    if (!form.tecnico_id) return alert('Selecciona un técnico');
    if (form.fecha_hasta < form.fecha_desde) return alert('La fecha de término es anterior al inicio');
    setGuardando(true);
    try {
      await api.post('/planificacion/turnos-emergencia', form);
      setForm(f => ({ ...f, tecnico_id: '', telefono: '', nota: '' }));
      cargarTurnos();
      onCambio();
    } catch (e) {
      alert(e.response?.data?.error || 'No se pudo guardar el turno');
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este turno?')) return;
    try {
      await api.delete(`/planificacion/turnos-emergencia/${id}`);
      cargarTurnos();
      onCambio();
    } catch {
      alert('No se pudo eliminar');
    }
  };

  const hoy = hoyChile();
  const input = {
    width: '100%', padding: '9px 10px', border: '1.5px solid #2E4A40', borderRadius: 8,
    fontSize: 14, background: '#0D1311', color: '#F4F7F5', fontFamily: 'inherit',
    boxSizing: 'border-box',
  };
  const label = { fontSize: 12, color: '#8A9994', marginBottom: 4, display: 'block' };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#111814', borderRadius: 16, width: '100%', maxWidth: 560,
        maxHeight: '90vh', overflowY: 'auto', padding: 20, border: '1px solid #1E2922',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#F4F7F5' }}>🚨 Turnos de emergencia</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#8A9994', cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={label}>Técnico de turno</label>
            <select value={form.tecnico_id} style={input}
              onChange={e => setForm(f => ({ ...f, tecnico_id: e.target.value }))}>
              <option value="">— Seleccionar —</option>
              {tecnicos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
          </div>
          <div>
            <label style={label}>Desde</label>
            <input type="date" value={form.fecha_desde} style={input}
              onChange={e => setForm(f => ({
                ...f, fecha_desde: e.target.value,
                fecha_hasta: f.fecha_hasta < e.target.value ? e.target.value : f.fecha_hasta,
              }))} />
          </div>
          <div>
            <label style={label}>Hasta</label>
            <input type="date" value={form.fecha_hasta} min={form.fecha_desde} style={input}
              onChange={e => setForm(f => ({ ...f, fecha_hasta: e.target.value }))} />
          </div>
          <div>
            <label style={label}>Teléfono de contacto (opcional)</label>
            <input value={form.telefono} placeholder="+56 9 ..." style={input}
              onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))} />
          </div>
          <div>
            <label style={label}>Nota (opcional)</label>
            <input value={form.nota} placeholder="Ej: turno fin de semana" style={input}
              onChange={e => setForm(f => ({ ...f, nota: e.target.value }))} />
          </div>
        </div>
        <button onClick={guardar} disabled={guardando} style={{
          marginTop: 12, width: '100%', background: guardando ? '#6B7C74' : '#0f6e56',
          border: 'none', borderRadius: 10, padding: '10px 14px', color: 'white',
          fontWeight: 700, fontSize: 14, cursor: 'pointer',
        }}>
          {guardando ? 'Guardando…' : '+ Asignar turno'}
        </button>

        <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7C74', textTransform: 'uppercase',
                      letterSpacing: '0.8px', margin: '20px 0 8px' }}>
          Vigentes y próximos
        </div>
        {turnos.length === 0 ? (
          <p style={{ color: '#6B7C74', fontSize: 13 }}>No hay turnos asignados.</p>
        ) : turnos.map(t => {
          const vigente = t.fecha_desde <= hoy && hoy <= t.fecha_hasta;
          return (
            <div key={t.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              borderRadius: 10, marginBottom: 6, background: '#0D1311',
              border: `1px solid ${vigente ? '#3D1515' : 'rgba(255,255,255,0.06)'}`,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#F4F7F5' }}>
                  {t.tecnico_nombre}
                  {vigente && <span style={{ marginLeft: 8, fontSize: 11, color: '#EF4444', fontWeight: 700 }}>● HOY</span>}
                </div>
                <div style={{ fontSize: 12, color: '#8A9994', marginTop: 2 }}>
                  {rango(t)}{t.telefono ? ` · ${t.telefono}` : ''}{t.nota ? ` · ${t.nota}` : ''}
                </div>
              </div>
              <button onClick={() => eliminar(t.id)} title="Eliminar" style={{
                background: '#2D0F0F', border: '1px solid #3D1515', borderRadius: 8,
                padding: '6px 9px', color: '#EF4444', cursor: 'pointer', display: 'flex',
              }}>
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
